import { Megaphone, MoreHorizontal, Plus, Search, Settings2, Trash2 } from "lucide-react";
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { CopyButton } from "@/components/ui/misc";
import {
  MonoCell, Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { Page, PageHeader, Toolbar } from "@/components/layout/page";
import { CampaignFormDialog } from "@/features/campaign-form";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { dialModeText, purposeText, statusText } from "@/lib/campaign";
import { useCampaigns, useDeleteCampaign } from "@/lib/queries";
import { atLeast, type Campaign } from "@/lib/types";
import { useDebounced } from "@/lib/use-debounced";

/**
 * Campaigns: the firms whose leads this organisation works. Each one's name opens its page, where
 * its lines, staffing, dispositions, dialing and lists are set (`routes/campaign.tsx`); this list
 * says at a glance which campaigns the dialer is working.
 */
export function CampaignsPage() {
  const { user: actor } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const canManage = atLeast(actor?.role, "admin");
  const isSuperadmin = actor?.role === "superadmin";

  const [search, setSearch] = React.useState("");
  const debounced = useDebounced(search, 250);
  const { data, isPending, error, refetch } = useCampaigns({ q: debounced || undefined });

  const [formOpen, setFormOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Campaign | undefined>();
  const [deleting, setDeleting] = React.useState<Campaign | undefined>();
  const remove = useDeleteCampaign();

  return (
    <Page>
      <PageHeader
        title="Campaigns"
        description="The firms whose leads you work. Open one to set how its calls are routed, who takes them, and how its leads are dialed."
        action={
          canManage && (
            <Button
              variant="default"
              onClick={() => {
                setEditing(undefined);
                setFormOpen(true);
              }}
            >
              <Plus />
              New campaign
            </Button>
          )
        }
      />

      <Card className="overflow-hidden">
        <Toolbar>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or slug"
            leading={<Search />}
            className="w-56"
            aria-label="Search campaigns"
          />
          <span className="ml-auto text-xs text-text-muted">
            {data ? `${data.length} campaign${data.length === 1 ? "" : "s"}` : null}
          </span>
        </Toolbar>

        {isPending ? (
          <TableSkeleton cols={8} />
        ) : error ? (
          <ErrorState
            error={error}
            title="Could not load campaigns"
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                Try again
              </Button>
            }
          />
        ) : data.length === 0 ? (
          <EmptyState
            icon={Megaphone}
            title={debounced ? "No campaigns match" : "No campaigns yet"}
            description={
              debounced
                ? "Try a different name or slug."
                : "Create one per firm, then route its numbers to it."
            }
            action={
              canManage && !debounced ? (
                <Button variant="default" onClick={() => setFormOpen(true)}>
                  <Plus />
                  New campaign
                </Button>
              ) : undefined
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>State</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>For</TableHead>
                <TableHead>Dialer</TableHead>
                <TableHead>Numbers</TableHead>
                {isSuperadmin && <TableHead>Tenant</TableHead>}
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((c) => {
                const status = statusText(c.status);
                return (
                  <TableRow key={c.id}>
                    <TableCell>
                      <span className="inline-flex items-center gap-1.5">
                        <Link
                          to={`/campaigns/${c.id}`}
                          className="font-medium text-text underline-offset-4 hover:underline"
                        >
                          {c.name}
                        </Link>
                        {!c.enabled && (
                          <Badge tone="warning" size="sm">
                            Disabled
                          </Badge>
                        )}
                      </span>
                    </TableCell>
                    <MonoCell>
                      <span className="inline-flex items-center gap-1">
                        {c.slug}
                        <CopyButton value={c.slug} />
                      </span>
                    </MonoCell>
                    <TableCell className="text-text-muted">
                      {c.state ?? "—"}
                      <span className="ml-1.5 text-xs text-text-subtle">{c.language.toUpperCase()}</span>
                    </TableCell>
                    <TableCell>
                      <Badge tone={status.tone} size="sm">
                        {status.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-text-muted">
                      {purposeText(c.purpose).label}
                    </TableCell>
                    <TableCell className={c.dial_mode === "off" ? "text-text-subtle" : "text-text"}>
                      {dialModeText(c.dial_mode).label}
                    </TableCell>
                    <TableCell>
                      <Badge tone={c.did_count > 0 ? "neutral" : "outline"}>{c.did_count}</Badge>
                    </TableCell>
                    {isSuperadmin && (
                      <TableCell className="text-xs text-text-muted">{c.tenant_name ?? "—"}</TableCell>
                    )}
                    <TableCell>
                      {canManage && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm" aria-label={`Actions for ${c.name}`}>
                              <MoreHorizontal />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent>
                            <DropdownMenuItem
                              onSelect={() => {
                                setEditing(c);
                                setFormOpen(true);
                              }}
                            >
                              <Settings2 />
                              Edit details
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem destructive onSelect={() => setDeleting(c)}>
                              <Trash2 />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <CampaignFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        campaign={editing}
        onCreated={(created) => navigate(`/campaigns/${created.id}`)}
      />

      <ConfirmDialog
        open={Boolean(deleting)}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title="Delete this campaign?"
        description={
          <>
            <span className="font-medium">{deleting?.name}</span> and its lines will be removed.
            Numbers routed to it or tagged with it block this — re-route them first.
          </>
        }
        confirmLabel="Delete campaign"
        destructive
        loading={remove.isPending}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await remove.mutateAsync(deleting.id);
            toast.success("Campaign deleted", deleting.name);
            setDeleting(undefined);
          } catch (e) {
            toast.error("Could not delete", e instanceof ApiError ? e.message : "Please try again.");
          }
        }}
      />
    </Page>
  );
}
